import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Popup, PopupType } from '../popup';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';
import { ConfirmationPopupModel } from '../confirmation-popup/confirmation-popup.model';
import { InfoPopupComponent } from '../info-popup/info-popup.component';
import { InfoPopupModel } from '../info-popup/info-popup.model';
import { UserInputPopupComponent } from '../user-input-popup/user-input-popup.component';
import { UserInputPopupModel } from '../user-input-popup/user-input-popup.model';
import { AddPropertyPopupComponent } from '../../../../settings/properties/components/popups/add-property-popup/add-property-popup.component';
import { AddPropertyPopupModel } from '../../../../settings/properties/components/popups/add-property-popup/add-property-popup.model';
import { PropertyConnectionSuccessPopupComponent } from '../../../../settings/properties/components/popups/property-connection-success-popup/property-connection-success-popup.component';
import { PropertyConnectionSuccessPopupModel } from '../../../../settings/properties/components/popups/property-connection-success-popup/property-connection-success-popup.model';
import { ManagePmsPopupComponent } from '../../../../settings/properties/components/popups/manage-pms-popup/manage-pms-popup.component';
import { ManagePmsPopupModel } from '../../../../settings/properties/components/popups/manage-pms-popup/manage-pms-popup.model';
import { DeleteOrganizationPopupComponent } from '../../../../settings/organization-settings/popups/delete-organization-popup/delete-organization-popup.component';
import { DeleteOrganizationPopupModel } from '../../../../settings/organization-settings/popups/delete-organization-popup/delete-organization-popup.model';
import { ViewTermsPopupComponent } from '../../../../settings/organization-settings/popups/view-terms-popup/view-terms-popup.component';
import { ViewTermsPopupModel } from '../../../../settings/organization-settings/popups/view-terms-popup/view-terms-popup.model';
import { TwoFactorSetupPopupComponent } from '../../../../settings/account-settings/security/popups/two-factor-setup-popup/two-factor-setup-popup.component';
import { TwoFactorSetupPopupModel } from '../../../../settings/account-settings/security/popups/two-factor-setup-popup/two-factor-setup-popup.model';
import { RequestAccessPopupComponent } from '../../../../settings/account-settings/my-access/popups/request-access-popup/request-access-popup.component';
import { RequestAccessPopupModel } from '../../../../settings/account-settings/my-access/popups/request-access-popup/request-access-popup.model';
import { LayoutStore, LayoutStoreType } from '../../../../core/store/layout.store';

@Component({
    selector: 'juyo-popup-container',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ConfirmationPopupComponent,
        InfoPopupComponent,
        UserInputPopupComponent,
        AddPropertyPopupComponent,
        PropertyConnectionSuccessPopupComponent,
        ManagePmsPopupComponent,
        DeleteOrganizationPopupComponent,
        ViewTermsPopupComponent,
        TwoFactorSetupPopupComponent,
        RequestAccessPopupComponent,
    ],
    template: `
        @for (popup of layoutStore.popups(); track popup.id) {
            @switch (popup.type) {
                @case (PopupType.Confirmation) {
                    <juyo-confirmation-popup [model]="asConfirmation(popup)"></juyo-confirmation-popup>
                }
                @case (PopupType.Info) {
                    <juyo-info-popup [model]="asInfo(popup)"></juyo-info-popup>
                }
                @case (PopupType.UserInput) {
                    <juyo-user-input-popup [model]="asUserInput(popup)"></juyo-user-input-popup>
                }
                @case (PopupType.AddProperty) {
                    <juyo-add-property-popup [model]="asAddProperty(popup)"></juyo-add-property-popup>
                }
                @case (PopupType.PropertyConnectionSuccess) {
                    <juyo-property-connection-success-popup
                        [model]="asPropertyConnectionSuccess(popup)"
                    ></juyo-property-connection-success-popup>
                }
                @case (PopupType.ManagePms) {
                    <juyo-manage-pms-popup [model]="asManagePms(popup)"></juyo-manage-pms-popup>
                }
                @case (PopupType.DeleteOrganization) {
                    <juyo-delete-organization-popup
                        [model]="asDeleteOrganization(popup)"
                    ></juyo-delete-organization-popup>
                }
                @case (PopupType.ViewTerms) {
                    <juyo-view-terms-popup [model]="asViewTerms(popup)"></juyo-view-terms-popup>
                }
                @case (PopupType.TwoFactorSetup) {
                    <juyo-two-factor-setup-popup
                        [model]="asTwoFactorSetup(popup)"
                    ></juyo-two-factor-setup-popup>
                }
                @case (PopupType.RequestAccess) {
                    <juyo-request-access-popup [model]="asRequestAccess(popup)"></juyo-request-access-popup>
                }
            }
        }
    `,
})
export class PopupContainerComponent {
    protected layoutStore: LayoutStoreType = inject(LayoutStore);
    protected readonly PopupType = PopupType;

    protected asConfirmation(popup: Popup): ConfirmationPopupModel {
        return popup as ConfirmationPopupModel;
    }

    protected asInfo(popup: Popup): InfoPopupModel {
        return popup as InfoPopupModel;
    }

    protected asUserInput(popup: Popup): UserInputPopupModel {
        return popup as UserInputPopupModel;
    }

    protected asAddProperty(popup: Popup): AddPropertyPopupModel {
        return popup as AddPropertyPopupModel;
    }

    protected asPropertyConnectionSuccess(popup: Popup): PropertyConnectionSuccessPopupModel {
        return popup as PropertyConnectionSuccessPopupModel;
    }

    protected asManagePms(popup: Popup): ManagePmsPopupModel {
        return popup as ManagePmsPopupModel;
    }

    protected asDeleteOrganization(popup: Popup): DeleteOrganizationPopupModel {
        return popup as DeleteOrganizationPopupModel;
    }

    protected asViewTerms(popup: Popup): ViewTermsPopupModel {
        return popup as ViewTermsPopupModel;
    }

    protected asTwoFactorSetup(popup: Popup): TwoFactorSetupPopupModel {
        return popup as TwoFactorSetupPopupModel;
    }

    protected asRequestAccess(popup: Popup): RequestAccessPopupModel {
        return popup as RequestAccessPopupModel;
    }
}
